import React, { useState } from "react";
import jsPDF from "jspdf";

export default function BlogResult({ result }) {
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  const copyToClipboard = async () => {
    const fullContent = `BLOG OUTLINE:\n${result.outline}\n\nSEO & KEYWORDS:\n${result.seo}\n\nFULL BLOG CONTENT:\n${result.blog}`;
    
    try {
      await navigator.clipboard.writeText(fullContent.replace(/[#*]/g, ''));
      setCopied(true);
      setToastMessage('Blog content copied to clipboard!');
      setShowToast(true);
      setTimeout(() => {
        setCopied(false);
        setShowToast(false);
      }, 3000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = fullContent.replace(/[#*]/g, '');
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setToastMessage('Blog content copied to clipboard!');
      setShowToast(true);
      setTimeout(() => {
        setCopied(false);
        setShowToast(false);
      }, 3000);
    }
  };

  const downloadPDF = () => {
    setDownloading(true);
    
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 20;
      const maxWidth = pageWidth - 2 * margin;
      let yPosition = 30;
      
      // Title
      doc.setFontSize(18);
      doc.setFont(undefined, 'bold');
      doc.text('AI Generated Blog', margin, yPosition);
      yPosition += 20;
      
      // Outline
      doc.setFontSize(14);
      doc.setFont(undefined, 'bold');
      doc.text('Blog Outline:', margin, yPosition);
      yPosition += 10;
      
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      const outlineLines = doc.splitTextToSize(result.outline.replace(/[#*]/g, ''), maxWidth);
      doc.text(outlineLines, margin, yPosition);
      yPosition += outlineLines.length * 5 + 15;
      
      // SEO
      doc.setFontSize(14);
      doc.setFont(undefined, 'bold');
      doc.text('SEO & Keywords:', margin, yPosition);
      yPosition += 10;
      
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      const seoLines = doc.splitTextToSize(result.seo.replace(/[#*]/g, ''), maxWidth);
      doc.text(seoLines, margin, yPosition);
      yPosition += seoLines.length * 5 + 15;
      
      // Blog Content
      doc.setFontSize(14);
      doc.setFont(undefined, 'bold');
      doc.text('Full Blog Content:', margin, yPosition);
      yPosition += 10;
      
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      const contentLines = doc.splitTextToSize(result.blog.replace(/[#*]/g, ''), maxWidth);
      
      // Handle page breaks
      for (let i = 0; i < contentLines.length; i++) {
        if (yPosition > 270) {
          doc.addPage();
          yPosition = 30;
        }
        doc.text(contentLines[i], margin, yPosition);
        yPosition += 5;
      }
      
      doc.save('ai-blog-content.pdf');
      setToastMessage('PDF downloaded successfully!');
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
    } catch (err) {
      console.error('Failed to generate PDF:', err);
    } finally {
      setDownloading(false);
    }
  };
  return (
    <div className="max-w-6xl mx-auto mt-8 relative">
      {/* Toast Notification */}
      {showToast && (
        <div className="fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 animate-pulse">
          {toastMessage}
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Outline */}
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Blog Outline
          </h3>
          <div className="text-gray-700 text-sm leading-relaxed">
            {result.outline.replace(/[#*]/g, '').split('\n').map((line, index) => (
              <p key={index} className="mb-2">{line}</p>
            ))}
          </div>
        </div>

        {/* SEO */}
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            SEO & Keywords
          </h3>
          <div className="text-gray-700 text-sm leading-relaxed">
            {result.seo.replace(/[#*]/g, '').split('\n').map((line, index) => (
              <p key={index} className="mb-2">{line}</p>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Actions
          </h3>
          <div className="space-y-3">
            <button 
              onClick={copyToClipboard}
              className={`w-full py-2 px-4 rounded-lg transition text-sm ${
                copied 
                  ? 'bg-green-100 text-green-700' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {copied ? '✓ Copied!' : 'Copy to Clipboard'}
            </button>
            <button 
              onClick={downloadPDF}
              disabled={downloading}
              className="w-full bg-indigo-600 text-white py-2 px-4 rounded-lg hover:bg-indigo-700 transition text-sm disabled:opacity-50"
            >
              {downloading ? 'Generating...' : 'Download as PDF'}
            </button>
          </div>
        </div>
      </div>
      
      {/* Full Blog Content */}
      <div className="bg-white rounded-xl shadow-sm border p-8 mt-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-6">
          Generated Blog Content
        </h3>
        <div className="prose max-w-none">
          <div className="text-gray-900 leading-relaxed">
            {result.blog.replace(/[#*]/g, '').split('\n').map((line, index) => {
              if (line.trim() === '') return <br key={index} />;
              if (line.match(/^\d+\./)) {
                return <h4 key={index} className="text-lg font-semibold mt-6 mb-3 text-gray-800">{line}</h4>;
              }
              return <p key={index} className="mb-4 text-gray-700">{line}</p>;
            })}
          </div>
        </div>
      </div>
    </div>
  );
}